import { ASSISTANT_OUTCOMES, type AssistantOutcome } from "@sagot/constants";
import { decodeWorkflowRun, type ChatMessage } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { ChoiceChips } from "@sagot/ui";
import { useId, useRef, useState } from "react";
import { usePreviewOutcome } from "./use-preview";

const P = "engage.workflows.builder.preview.outcome";

interface SimulateOutcomeProps {
  workflowId: string;
  conversationId: string;
  /** The run that waits for the assistant. */
  runId: string;
  /** What the workflow said after the chosen outcome, ready to render. */
  onMessages: (messages: ChatMessage[]) => void;
}

/**
 * A run waiting at "Let the assistant answer" can also be steered by hand: pick how the answer goes and the run follows
 * that exit, without a question the knowledge base can (or cannot) answer. Typing a question still lets the real assistant answer.
 */
export function SimulateOutcome({ workflowId, conversationId, runId, onMessages }: SimulateOutcomeProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const choose = usePreviewOutcome(workflowId);
  /** The outcome being applied, then the one that was (the result is announced once). */
  const [chosen, setChosen] = useState<AssistantOutcome | null>(null);
  const [applied, setApplied] = useState<AssistantOutcome | null>(null);
  const served = useRef(0);
  const options = ASSISTANT_OUTCOMES.map((value) => ({ value, label: t(`${P}.${value}`), disabled: choose.isPending }));

  const pick = (outcome: AssistantOutcome) => {
    setChosen(outcome);
    setApplied(null);
    choose.mutate(
      { conversationId, runId, outcome },
      {
        onError: () => { setChosen(null); },
        onSuccess: (result) => {
          setChosen(null);
          setApplied(outcome);
          served.current += 1;
          const turn = served.current;
          // The run's own messages are numbered from 0 per run; these come after the ones already on screen.
          onMessages(decodeWorkflowRun(result).messages.map((message) => ({ ...message, id: `${message.id}:outcome-${String(turn)}` })));
        },
      },
    );
  };

  return (
    <section aria-labelledby={titleId} className="space-y-2 rounded-lg border border-line bg-surface p-3" data-testid="preview-outcome">
      <h3 id={titleId} className="text-xs font-bold">{t(`${P}.title`)}</h3>
      <p className="text-xs text-fg-muted">{t(`${P}.hint`)}</p>
      <ChoiceChips aria-label={t(`${P}.title`)} options={options} value={chosen} onValueChange={pick} />
      <p role="status" className="text-xs text-fg-muted">{applied ? t(`${P}.applied`, { outcome: t(`${P}.${applied}`) }) : null}</p>
      {choose.isError ? (
        <p role="alert" className="text-xs text-danger">
          <span className="font-semibold">{t(`${P}.failedTitle`)}.</span> {t(`${P}.failedBody`)}
        </p>
      ) : null}
    </section>
  );
}
